import { X } from "lucide-react";

export default function COADocument({ coa, onClose }) {
  if (!coa) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.85)' }}>
      <div
        data-testid="coa-modal"
        className="relative w-full max-w-[700px] max-h-[90vh] overflow-y-auto rounded-lg"
        style={{ background: '#ffffff', color: '#222' }}
      >
        {/* Close button */}
        <button
          data-testid="coa-close-btn"
          onClick={onClose}
          className="absolute top-3 right-3 z-10 w-8 h-8 flex items-center justify-center rounded-full"
          style={{ background: '#f0f0f0', color: '#333' }}
        >
          <X className="w-4 h-4" />
        </button>

        <div className="p-8 sm:p-10" style={{ fontFamily: "'Arial', 'Helvetica', sans-serif" }}>
          {/* Header */}
          <div className="flex items-start justify-between mb-1">
            <h1 className="text-3xl font-bold tracking-tight" style={{ color: '#222' }}>
              TEST REPORT
            </h1>
            <div className="flex flex-col items-center">
              <svg width="40" height="28" viewBox="0 0 40 28" fill="none">
                <path d="M15 28 L15 8 L20 0 L25 8 L25 28" stroke="#4CAF50" strokeWidth="2" fill="none"/>
                <path d="M10 28 L10 14 L15 8" stroke="#4CAF50" strokeWidth="1.5" fill="none"/>
                <path d="M30 28 L30 14 L25 8" stroke="#4CAF50" strokeWidth="1.5" fill="none"/>
                <path d="M5 28 L5 18 L10 14" stroke="#8BC34A" strokeWidth="1.5" fill="none"/>
                <path d="M35 28 L35 18 L30 14" stroke="#8BC34A" strokeWidth="1.5" fill="none"/>
              </svg>
              <span className="text-sm font-bold tracking-wider" style={{ color: '#333' }}>JANOSHIK</span>
            </div>
            <div className="text-right text-[10px] leading-relaxed" style={{ color: '#666' }}>
              E-mail: info@janoshik.com<br />
              Web: www.janoshik.com
            </div>
          </div>

          {/* Green line */}
          <div className="h-[2px] mb-6" style={{ background: '#4CAF50' }} />

          {/* Task info */}
          <div className="flex items-start justify-between mb-6">
            <div className="flex items-center gap-3">
              <span className="text-sm font-semibold" style={{ color: '#4CAF50' }}>Task Number</span>
              <span className="text-sm font-bold px-4 py-1" style={{ background: '#f5f5f5', color: '#222' }}>
                {coa.taskNumber}
              </span>
            </div>
            <div className="text-right text-xs">
              <div>
                <span style={{ color: '#4CAF50' }}>Testing ordered &nbsp;&gt;&nbsp;</span>
                <span className="font-bold px-2 py-0.5" style={{ background: '#f5f5f5' }}>{coa.testingOrdered}</span>
              </div>
              <div className="mt-1">
                <span style={{ color: '#4CAF50' }}>Sample received &nbsp;&gt;&nbsp;</span>
                <span className="font-bold px-2 py-0.5" style={{ background: '#f5f5f5' }}>{coa.sampleReceived}</span>
              </div>
            </div>
          </div>

          {/* Client / Sample info */}
          <div className="space-y-2 mb-6">
            {[
              { label: "Client", value: coa.client },
              { label: "Sample", value: coa.sample },
              { label: "Manufacturer", value: coa.manufacturer },
              { label: "Batch", value: coa.batch },
            ].map(({ label, value }) => (
              <div key={label} className="flex items-center">
                <span className="text-sm w-32 flex-shrink-0" style={{ color: '#4CAF50' }}>{label}</span>
                <span className="text-sm font-semibold flex-1 px-3 py-1" style={{ background: '#f5f5f5', color: '#222' }}>
                  {value}
                </span>
              </div>
            ))}
          </div>

          {/* Sample description */}
          <div className="mb-5">
            <p className="text-sm mb-2" style={{ color: '#4CAF50' }}>Sample description &gt;</p>
            <div className="px-3 py-2 text-sm" style={{ background: '#f5f5f5', color: '#444' }}>
              See picture or pictures attached.
            </div>
          </div>

          {/* Tests requested */}
          <div className="mb-5">
            <p className="text-sm mb-2" style={{ color: '#4CAF50' }}>Tests requested &gt;</p>
            <div className="px-3 py-2 text-sm" style={{ background: '#f5f5f5', color: '#444' }}>
              Assessment of a peptide vial or vials.
            </div>
          </div>

          {/* Results */}
          <div className="mb-5">
            <p className="text-sm mb-3" style={{ color: '#4CAF50' }}>Results &gt;</p>
            <table className="w-full text-sm" style={{ borderCollapse: 'collapse' }}>
              <tbody>
                <tr>
                  <td className="px-3 py-2 font-medium" style={{ border: '1px solid #4CAF50', color: '#222' }}>
                    {coa.compoundName}
                  </td>
                  <td className="px-3 py-2 font-semibold" style={{ border: '1px solid #4CAF50', color: '#222' }}>
                    {coa.measuredAmount}
                  </td>
                </tr>
                <tr>
                  <td className="px-3 py-2 font-medium" style={{ border: '1px solid #4CAF50', color: '#222' }}>
                    Purity
                  </td>
                  <td className="px-3 py-2 font-semibold" style={{ border: '1px solid #4CAF50', color: '#222' }}>
                    {coa.purity}
                  </td>
                </tr>
                <tr>
                  <td className="px-3 py-2" style={{ border: '1px solid #4CAF50' }}>&nbsp;</td>
                  <td className="px-3 py-2" style={{ border: '1px solid #4CAF50' }}>&nbsp;</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Comments */}
          <div className="mb-8">
            <p className="text-sm mb-2" style={{ color: '#4CAF50' }}>Comments &gt;</p>
            <div className="px-3 py-4 text-sm" style={{ background: '#f5f5f5', color: '#444', minHeight: '40px' }}>
            </div>
          </div>

          {/* Analysis conducted + Signature */}
          <div className="flex items-end justify-between mb-8">
            <div>
              <span className="text-sm" style={{ color: '#4CAF50' }}>Analysis conducted &gt; </span>
              <span className="text-sm font-bold px-3 py-1" style={{ background: '#f5f5f5', color: '#222' }}>
                {coa.analysisDate}
              </span>
            </div>
            <div className="text-right">
              <span className="text-sm" style={{ color: '#4CAF50' }}>Signature &gt;</span>
              <div className="mt-1" style={{ fontFamily: "'Brush Script MT', 'Segoe Script', cursive", fontSize: '24px', color: '#333' }}>
                Jano
              </div>
            </div>
          </div>

          {/* Verification */}
          <div className="text-center">
            <p className="text-xs mb-2" style={{ color: '#4CAF50' }}>
              Verify this test at www.janoshik.com/verify/ with the following unique key
            </p>
            <div className="inline-block px-6 py-1.5 text-sm font-bold tracking-wider" style={{ background: '#f5f5f5', color: '#222' }}>
              {coa.verifyKey}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
